/**
 * [INPUT]: 依赖 react，依赖 react-router-dom 的 Link/useNavigate，依赖 ./Footer 与 ../seo 的 ROUTE_SEO/SITE/RouteSeo
 * [OUTPUT]: 对外提供 LegalPageKey 类型与 LegalPage 组件，渲染 Contact / Terms of service / Privacy 三个法律页
 * [POS]: apps/web/components 的静态法律页，经 RR7 框架模式路由 /contact /terms /privacy 挂载
 *   - head 由 route 文件 (app/routes/{contact,terms,privacy}.tsx) 的 meta/links export 注入，组件内不再挂 <Seo>
 * [PROTOCOL]: 变更时更新本头部，然后检查 ../CLAUDE.md
 */
import { Link, useNavigate } from 'react-router-dom'
import { Footer } from './Footer'
import { ROUTE_SEO, SITE, type RouteSeo } from '../seo'

export type LegalPageKey = 'contact' | 'terms' | 'privacy'

interface Props {
  page: LegalPageKey
}

interface LegalDoc {
  title: string
  sections: { heading: string; body: string }[]
  seo: RouteSeo
}

const DOCS: Record<LegalPageKey, LegalDoc> = {
  contact: {
    title: 'Contact',
    seo: ROUTE_SEO.contact,
    sections: [
      {
        heading: 'Get in Touch',
        body:
          'If you have any questions about our scoring methodology, privacy architecture, or collaboration opportunities, please reach out to the PSL Analyzer team using the channels below. We aim to respond within a reasonable timeframe.',
      },
      {
        heading: 'Email',
        body: `${SITE.contactEmail} — for product feedback, bug reports, and business inquiries.`,
      },
      {
        heading: 'Website',
        body: 'https://pslanalyzer.com — free on-device PSL facial attractiveness scoring.',
      },
      {
        heading: 'Feedback & Troubleshooting',
        body:
          'Since all scoring runs locally in your browser, if you encounter unexpected detection results, please include your browser version, device model, and photo conditions (front-facing / eye-level / lighting) so we can investigate the rule thresholds.',
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    seo: ROUTE_SEO.terms,
    sections: [
      {
        heading: 'Nature of Service',
        body:
          'PSL Analyzer provides a heuristic estimation tool based on facial geometric proportions. All computation is performed locally in your browser. This service is for entertainment and educational reference only and does not constitute medical diagnosis, cosmetic surgery advice, professional employment criteria, or any form of scientific qualification.',
      },
      {
        heading: 'Conditions of Use',
        body:
          'You agree to upload only photos for which you hold legitimate rights and not to use this service for infringing on others\' portrait rights, harassment, discrimination, or any unlawful purpose. By using this service, you acknowledge and accept these limitations.',
      },
      {
        heading: 'Disclaimer of Warranty',
        body:
          'This site makes no warranties, express or implied, regarding the accuracy, completeness, or fitness for any particular purpose of the scoring results. Under no circumstances shall the site or its operators be liable for any direct or indirect damages arising from the use of or reliance on the results of this service.',
      },
      {
        heading: 'Changes to Terms',
        body: 'We reserve the right to revise these terms at any time. Revised terms take effect upon publication. Continued use of the service constitutes acceptance of the revised terms.',
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    seo: ROUTE_SEO.privacy,
    sections: [
      {
        heading: 'On-Device First: Your Photo Never Leaves',
        body:
          'PSL Analyzer uses a pure client-side computing architecture. Facial analysis, geometric ratio calculation, and scoring all happen locally in your browser. Your original photo never leaves your device and is never uploaded to any server.',
      },
      {
        heading: 'Data Sent for AI Expert Diagnosis',
        body:
          'When you actively tap "AI Expert Diagnosis," the system sends anonymized on-device scoring results to the backend to generate a diagnostic report. The payload includes only: scorer version, inferred gender, total score, confidence score, and 8 dimensionless metric values. We never send your original photo, pixel data, facial-point coordinates, or any data from which a face could be reconstructed.',
      },
      {
        heading: 'No Cookie Tracking',
        body: 'This site does not use third-party analytics cookies or advertising tracking pixels for cross-site behavioral tracking, nor does it collect personally identifiable information.',
      },
      {
        heading: 'Data Storage',
        body:
          'Since scoring is performed locally, we do not store your photos or scoring results on our servers. The anonymized metrics in AI diagnosis requests are used only for real-time report generation and are not retained long-term.',
      },
      {
        heading: 'Contact',
        body: `If you have any questions about this privacy policy, please contact us at ${SITE.contactEmail}.`,
      },
    ],
  },
}

export function LegalPage({ page }: Props) {
  const doc = DOCS[page]
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col antialiased">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-950/75 sticky top-0 z-50 backdrop-blur-md transition-all duration-300">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brass to-amber flex items-center justify-center font-bold text-slate-950 shadow-lg shadow-brass/20">
              P
            </div>
            <span className="font-bold text-lg tracking-tight bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
              PSL Analyzer
            </span>
          </Link>
          <button
            onClick={() => navigate('/')}
            className="text-xs font-semibold text-slate-400 hover:text-white border border-slate-700 hover:border-slate-600 bg-slate-900/40 rounded-xl px-4 py-2 transition duration-200"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-grow px-6 py-16">
        <article className="max-w-3xl mx-auto space-y-10">
          <h1 className="text-4xl font-light tracking-tight text-white">{doc.title}</h1>
          <div className="space-y-8">
            {doc.sections.map((s) => (
              <section key={s.heading} className="space-y-2">
                <h2 className="text-lg font-semibold text-brass">{s.heading}</h2>
                <p className="text-slate-300 text-sm leading-relaxed">{s.body}</p>
              </section>
            ))}
          </div>
        </article>
      </main>

      <Footer />
    </div>
  )
}